/**
 * Link category enum + classifier (admin mirror of lib/easel/profile-links-serializer.js).
 *
 * Kept deliberately in sync with the backend classifier so the admin's "Auto-detected: …" hint
 * matches the category the public API will resolve when the artist leaves it unset. If you change
 * the rules here, change them there too (and vice-versa).
 */

export const LINK_CATEGORIES = ['shop', 'social', 'support', 'press', 'website', 'other'] as const

export type LinkCategory = (typeof LINK_CATEGORIES)[number]

export const LINK_CATEGORY_LABELS: Record<LinkCategory, string> = {
  shop: 'Shop',
  social: 'Social',
  support: 'Support',
  press: 'Press',
  website: 'Website',
  other: 'Other',
}

const SHOP_HOST = /(bigcartel|myarthaus|shopify|etsy|gumroad|bigcommerce|squarespace.*commerce)/i
const SHOP_PATH = /(shop|store|products|collections)/i
const SUPPORT_HOST = /(ko-?fi|patreon|buymeacoffee|paypal\.me)/i
const SOCIAL_HOST = /(instagram|tiktok|facebook|twitter|x\.com|youtube|pinterest|threads|bsky)/i

/**
 * URL-host category classifier — the same fallback the server runs when a link has no explicit
 * category. Returns null for a URL it can't parse (so the caller can show "no suggestion").
 */
export function classifyLinkCategory(url: string, website?: string | null): LinkCategory | null {
  let parsed: URL
  try {
    parsed = new URL((url || '').trim())
  } catch {
    return null
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null

  const host = parsed.host.replace(/^www\./, '').toLowerCase()
  const path = (parsed.pathname || '').toLowerCase()

  if (SHOP_HOST.test(host) || SHOP_PATH.test(path)) return 'shop'
  if (SUPPORT_HOST.test(host)) return 'support'
  if (SOCIAL_HOST.test(host)) return 'social'

  if (website) {
    const ownHost = website
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
    if (ownHost && host === ownHost) return 'website'
  }

  return 'other'
}
